import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api.js";
import type { CollectionItem, LocationContext } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { CharacterAvatar } from "../components/CharacterAvatar.js";
import { ChipEditor, parseList } from "../components/ChipEditor.js";
import { fileToWide } from "../lib/image.js";

const str = (v: unknown) => (typeof v === "string" ? v : "");
type Category = "venue" | "home" | "other";
const CATEGORY_LABEL: Record<Category, string> = { venue: "Venues", home: "Homes", other: "Other" };
const ACCEPT = "image/jpeg,image/png,image/webp";
const GALLERY_MAX = 24;

/** Coarse bucket for the sidebar filter. Uses the stored category, else infers
 * from the type/name so existing locations slot in sensibly. */
function categoryOf(item: CollectionItem): Category {
  const stored = str(item.category);
  if (stored === "venue" || stored === "home" || stored === "other") return stored;
  const hay = `${str(item.type)} ${str(item.name)}`.toLowerCase();
  if (/\b(home|house|apartment|bedroom|flat|residence|dorm|cabin|mansion|cottage)\b/.test(hay)) return "home";
  if (/\b(bar|club|pub|restaurant|cafe|diner|venue|store|shop|theater|theatre|arena|stadium|hall|court|office|school|gym|library|hotel|mall|market|studio|park)\b/.test(hay)) return "venue";
  return "other";
}

function parseGallery(raw: unknown): string[] {
  const s = str(raw);
  if (!s.trim()) return [];
  try {
    const arr = JSON.parse(s);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : [];
  } catch { return []; }
}

export function LocationsPanel({ projectId, onOpenScene, focusId }: {
  projectId: string;
  onOpenScene: (sceneId: string) => void;
  focusId?: string | null;
}) {
  const toast = useToast();
  const [items, setItems] = useState<CollectionItem[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState<"all" | Category>("all");
  const [pendingDelete, setPendingDelete] = useState<CollectionItem | null>(null);

  const load = useCallback(async () => {
    const { items: list } = await api.listCollection(projectId, "locations");
    setItems(list);
    setSelectedId((cur) => cur ?? focusId ?? list[0]?.id ?? null);
  }, [projectId, focusId]);

  useEffect(() => {
    setItems(null); setSelectedId(null); setQuery(""); setCat("all");
    load().catch((err) => toast.error(err instanceof Error ? err.message : "Failed to load"));
  }, [load, toast]);
  useEffect(() => { if (focusId) setSelectedId(focusId); }, [focusId]);

  const counts = useMemo(() => {
    const c = { all: 0, venue: 0, home: 0, other: 0 };
    for (const it of items ?? []) { c.all++; c[categoryOf(it)]++; }
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items ?? [])
      .filter((it) => cat === "all" || categoryOf(it) === cat)
      .filter((it) => !q || str(it.name).toLowerCase().includes(q) || parseList(it.aliases).some((a) => a.toLowerCase().includes(q)))
      .sort((a, b) => {
        const fa = a.favorite ? 1 : 0, fb = b.favorite ? 1 : 0;
        if (fa !== fb) return fb - fa;
        return str(a.name).localeCompare(str(b.name));
      });
  }, [items, query, cat]);

  const selected = items?.find((i) => i.id === selectedId) ?? null;

  const create = async () => {
    try {
      const { item } = await api.createCollectionItem(projectId, "locations", { name: "New Location" });
      setItems((prev) => (prev ? [...prev, item] : [item]));
      setSelectedId(item.id);
    } catch (err) { toast.error(err instanceof Error ? err.message : "Failed to create"); }
  };

  const onChanged = (updated: CollectionItem) =>
    setItems((prev) => (prev ? prev.map((i) => (i.id === updated.id ? updated : i)) : prev));

  const toggleFavorite = async (item: CollectionItem) => {
    const next = !item.favorite;
    onChanged({ ...item, favorite: next ? 1 : 0 });
    try { const { item: u } = await api.updateCollectionItem("locations", item.id, { favorite: next }); onChanged(u); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Failed"); void load(); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id; setPendingDelete(null);
    try {
      await api.deleteCollectionItem("locations", id);
      setItems((prev) => {
        const next = prev ? prev.filter((i) => i.id !== id) : prev;
        if (selectedId === id) setSelectedId(next?.[0]?.id ?? null);
        return next;
      });
      toast.success("Location deleted.");
    } catch (err) { toast.error(err instanceof Error ? err.message : "Failed to delete"); }
  };

  return (
    <div className="loc">
      <div className="loc-list">
        <div className="loc-list-head">
          <div className="loc-list-title"><Icon name="map" size={18} style={{ color: "var(--accent)" }} /> <span>Locations</span></div>
          <button className="btn btn-icon btn-primary btn-sm" title="New location" onClick={create}><Icon name="plus" size={15} /></button>
        </div>
        <div className="loc-search">
          <Icon name="search" size={14} />
          <input placeholder="Search locations…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="loc-cats">
          {(["all", "venue", "home", "other"] as const).map((k) => (
            <button key={k} className={`loc-cat ${cat === k ? "active" : ""}`} onClick={() => setCat(k)}>
              {k === "all" ? "All" : CATEGORY_LABEL[k]} <span className="loc-cat-n">{counts[k]}</span>
            </button>
          ))}
        </div>
        <div className="loc-items">
          {items === null ? (
            <div className="collection-loading"><Icon name="clock" size={16} className="spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="collection-empty-list">
              <p className="muted">{query || cat !== "all" ? "No matches." : "No locations yet."}</p>
              {!query && cat === "all" && <button className="btn btn-sm" onClick={create}><Icon name="plus" size={14} /> New location</button>}
            </div>
          ) : (
            filtered.map((item) => (
              <div key={item.id} className={`loc-item ${item.id === selectedId ? "active" : ""}`}>
                <button className="loc-item-main" onClick={() => setSelectedId(item.id)}>
                  <span className="loc-thumb">
                    {item.imageData ? <img src={str(item.imageData)} alt="" /> : <Icon name="map" size={16} />}
                  </span>
                  <span className="grow" style={{ minWidth: 0 }}>
                    <span className="loc-item-name truncate">
                      {str(item.name) || "(untitled)"}
                      {item.origin === "ai" && <span className="loc-ai">AI</span>}
                    </span>
                    <span className="loc-item-sub truncate">{str(item.type) || CATEGORY_LABEL[categoryOf(item)]}</span>
                  </span>
                </button>
                <button
                  className={`loc-star ${item.favorite ? "on" : ""}`}
                  title={item.favorite ? "Unfavorite" : "Favorite"}
                  onClick={() => void toggleFavorite(item)}
                >
                  <Icon name="star" size={15} filled={!!item.favorite} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="loc-detail-wrap">
        {selected ? (
          <LocationDetail
            key={selected.id}
            item={selected}
            onChanged={onChanged}
            onDelete={() => setPendingDelete(selected)}
            onOpenScene={onOpenScene}
          />
        ) : (
          <div className="collection-detail-empty">
            <Icon name="map" size={40} style={{ color: "var(--text-faint)" }} />
            <h2>Locations</h2>
            <p className="muted">Select a location, or create one.</p>
            <button className="btn btn-primary" onClick={create}><Icon name="plus" size={15} /> New location</button>
          </div>
        )}
      </div>

      {pendingDelete && (
        <ConfirmDialog
          title="Delete location"
          message={`Delete “${str(pendingDelete.name) || "this location"}”? This cannot be undone.`}
          confirmLabel="Delete" danger
          onConfirm={confirmDelete} onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}

type Tab = "details" | "scenes" | "characters" | "gallery" | "notes";

function LocationDetail({ item, onChanged, onDelete, onOpenScene }: {
  item: CollectionItem;
  onChanged: (item: CollectionItem) => void;
  onDelete: () => void;
  onOpenScene: (sceneId: string) => void;
}) {
  const toast = useToast();
  const [tab, setTab] = useState<Tab>("details");
  const [ctx, setCtx] = useState<LocationContext | null>(null);
  const [saving, setSaving] = useState(false);
  const heroInput = useRef<HTMLInputElement>(null);
  const galInput = useRef<HTMLInputElement>(null);

  // Local field state so typing is smooth; commit on blur.
  const [vals, setVals] = useState<Record<string, string>>(() => ({
    name: str(item.name), type: str(item.type), description: str(item.description),
    visualDetails: str(item.visualDetails), notes: str(item.notes)
  }));
  const set = (k: string, v: string) => setVals((s) => ({ ...s, [k]: v }));

  const gallery = parseGallery(item.gallery);
  const aliases = parseList(item.aliases);
  const tags = parseList(item.tags);
  const category = categoryOf(item);

  useEffect(() => {
    let cancelled = false;
    setCtx(null);
    api.locationContext(item.id).then((c) => !cancelled && setCtx(c)).catch(() => !cancelled && setCtx(null));
    return () => { cancelled = true; };
  }, [item.id]);

  const persist = async (patch: Record<string, unknown>) => {
    setSaving(true);
    try { const { item: u } = await api.updateCollectionItem("locations", item.id, patch); onChanged(u); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Failed to save"); }
    finally { setSaving(false); }
  };
  const commit = (key: string, value: string) => {
    if ((str(item[key])) === value) return;
    void persist({ [key]: value || null });
  };

  const pickHero = async (file: File | null) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { toast.error("Use a JPG, PNG, or WebP image."); return; }
    try { await persist({ imageData: await fileToWide(file) }); toast.success("Image updated."); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't set image"); }
  };
  const addGallery = async (file: File | null) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { toast.error("Use a JPG, PNG, or WebP image."); return; }
    if (gallery.length >= GALLERY_MAX) { toast.error(`Up to ${GALLERY_MAX} images.`); return; }
    try { await persist({ gallery: JSON.stringify([...gallery, await fileToWide(file, 1400)]) }); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't add image"); }
  };
  const removeGallery = (idx: number) => void persist({ gallery: JSON.stringify(gallery.filter((_, i) => i !== idx)) });

  const sceneCount = ctx?.sceneCount ?? 0;
  const charCount = ctx?.characterCount ?? 0;

  return (
    <div className="loc-detail">
      <div className="loc-hero" style={item.imageData ? { backgroundImage: `url(${str(item.imageData)})` } : undefined}>
        <div className="loc-hero-shade" />
        <div className="loc-hero-top">
          <div className="loc-crumb"><Icon name="book" size={12} /> Story Bible <Icon name="chevronRight" size={11} /> Locations <Icon name="chevronRight" size={11} /> {str(item.name) || "Untitled"}</div>
          <div className="row gap-2">
            <button className="btn btn-sm btn-ghost ov-glass" onClick={() => heroInput.current?.click()}>
              <Icon name="image" size={13} /> {item.imageData ? "Edit image" : "Add image"}
            </button>
            <button className="btn btn-icon btn-ghost ov-glass btn-danger" title="Delete location" onClick={onDelete}><Icon name="trash" size={14} /></button>
          </div>
        </div>
        <div className="loc-hero-body">
          <h1 className="loc-hero-name">{str(item.name) || "Untitled"}</h1>
          <div className="loc-hero-type"><Icon name="pin" size={13} /> {str(item.type) || CATEGORY_LABEL[category]}</div>
        </div>
        <input ref={heroInput} type="file" accept={ACCEPT} style={{ display: "none" }}
          onChange={(e) => { void pickHero(e.target.files?.[0] ?? null); e.target.value = ""; }} />
      </div>

      <div className="loc-tabs">
        <LocTab id="details" tab={tab} setTab={setTab} icon="info" label="Details" />
        <LocTab id="scenes" tab={tab} setTab={setTab} icon="file" label="Scenes" count={sceneCount} />
        <LocTab id="characters" tab={tab} setTab={setTab} icon="users" label="Characters" count={charCount} />
        <LocTab id="gallery" tab={tab} setTab={setTab} icon="image" label="Gallery" count={gallery.length} />
        <LocTab id="notes" tab={tab} setTab={setTab} icon="note" label="Notes" />
        <span className="grow" />
        <span className="detail-saved faint">{saving ? "Saving…" : "Saved"}</span>
      </div>

      <div className="loc-body">
        <div className="loc-main">
          {tab === "details" && (
            <div className="loc-fields">
              <div className="loc-row2">
                <div className="field">
                  <label className="label">Type</label>
                  <input className="input" value={vals.type} placeholder="City, building, bar…"
                    onChange={(e) => set("type", e.target.value)} onBlur={(e) => commit("type", e.target.value)} />
                </div>
                <div className="field">
                  <label className="label">Category</label>
                  <select className="select" value={category}
                    onChange={(e) => void persist({ category: e.target.value })}>
                    {(["venue", "home", "other"] as Category[]).map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
                  </select>
                </div>
              </div>

              <div className="field">
                <label className="label">Aliases</label>
                <ChipEditor chips={aliases} placeholder="Add alias" onChange={(next) => void persist({ aliases: next.join(", ") || null })} />
              </div>

              <div className="field">
                <label className="label">Description</label>
                <textarea className="textarea" rows={4} value={vals.description} placeholder="What is this place?"
                  onChange={(e) => set("description", e.target.value)} onBlur={(e) => commit("description", e.target.value)} />
              </div>

              <div className="field">
                <label className="label">Visual details</label>
                <textarea className="textarea" rows={3} value={vals.visualDetails} placeholder="Lighting, layout, atmosphere…"
                  onChange={(e) => set("visualDetails", e.target.value)} onBlur={(e) => commit("visualDetails", e.target.value)} />
              </div>

              <div className="field">
                <label className="label">Important events</label>
                <BulletEditor value={str(item.importantEvents)} onChange={(v) => void persist({ importantEvents: v || null })} />
              </div>

              <div className="field">
                <label className="label">Notes</label>
                <textarea className="textarea" rows={3} value={vals.notes} placeholder="Anything else to remember…"
                  onChange={(e) => set("notes", e.target.value)} onBlur={(e) => commit("notes", e.target.value)} />
              </div>
            </div>
          )}

          {tab === "scenes" && (
            <TabScenes ctx={ctx} onOpenScene={onOpenScene} />
          )}

          {tab === "characters" && (
            <TabCharacters ctx={ctx} />
          )}

          {tab === "gallery" && (
            <div className="loc-gallery">
              {gallery.map((src, i) => (
                <div key={i} className="loc-gallery-cell">
                  <img src={src} alt="" />
                  <button className="loc-gallery-del" title="Remove" onClick={() => removeGallery(i)}><Icon name="x" size={13} /></button>
                </div>
              ))}
              {gallery.length < GALLERY_MAX && (
                <button className="loc-gallery-add" onClick={() => galInput.current?.click()}>
                  <Icon name="plus" size={20} /><span>Add image</span>
                </button>
              )}
              <input ref={galInput} type="file" accept={ACCEPT} style={{ display: "none" }}
                onChange={(e) => { void addGallery(e.target.files?.[0] ?? null); e.target.value = ""; }} />
            </div>
          )}

          {tab === "notes" && (
            <div className="field">
              <label className="label">Notes</label>
              <textarea className="textarea" rows={14} value={vals.notes} placeholder="Freeform notes about this location…"
                onChange={(e) => set("notes", e.target.value)} onBlur={(e) => commit("notes", e.target.value)} />
            </div>
          )}
        </div>

        <aside className="loc-aside">
          <section className="card pad">
            <div className="loc-aside-head"><Icon name="info" size={15} style={{ color: "var(--accent)" }} /><h3>Quick info</h3></div>
            <dl className="loc-quick">
              <QuickRow icon="pin" label="Type" value={str(item.type) || CATEGORY_LABEL[category]} />
              {aliases[0] && <QuickRow icon="tag" label="Alias" value={aliases[0]} />}
              <QuickRow icon="file" label="Scenes" value={String(sceneCount)} />
              <QuickRow icon="users" label="Characters" value={String(charCount)} />
              <QuickRow icon="map" label="First appearance" value={ctx?.firstAppearance?.chapterTitle || "—"} />
              <QuickRow icon="map" label="Last appearance" value={ctx?.lastAppearance?.chapterTitle || "—"} />
            </dl>
          </section>

          <section className="card pad">
            <div className="loc-aside-head"><Icon name="tag" size={15} style={{ color: "var(--accent)" }} /><h3>Tags</h3></div>
            <ChipEditor chips={tags} placeholder="Add tag" accent onChange={(next) => void persist({ tags: next.join(", ") || null })} />
          </section>

          <section className="card pad">
            <div className="loc-aside-head"><Icon name="image" size={15} style={{ color: "var(--accent)" }} /><h3>Gallery</h3>
              <span className="grow" />
              <button className="btn btn-sm btn-ghost" onClick={() => galInput.current?.click()}><Icon name="plus" size={13} /> Add</button>
            </div>
            {gallery.length === 0 ? (
              <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>No images yet.</p>
            ) : (
              <div className="loc-aside-gallery">
                {gallery.slice(0, 6).map((src, i) => (
                  <button key={i} className="loc-aside-shot" onClick={() => setTab("gallery")}><img src={src} alt="" /></button>
                ))}
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

function LocTab({ id, tab, setTab, icon, label, count }: {
  id: Tab; tab: Tab; setTab: (t: Tab) => void; icon: Parameters<typeof Icon>[0]["name"]; label: string; count?: number;
}) {
  return (
    <button className={`loc-tab ${tab === id ? "active" : ""}`} onClick={() => setTab(id)}>
      <Icon name={icon} size={14} /> {label}
      {count != null && count > 0 && <span className="loc-tab-n">{count}</span>}
    </button>
  );
}

function QuickRow({ icon, label, value }: { icon: Parameters<typeof Icon>[0]["name"]; label: string; value: string }) {
  return (
    <div className="loc-quick-row">
      <dt><Icon name={icon} size={13} className="faint" /> {label}</dt>
      <dd className="truncate">{value}</dd>
    </div>
  );
}

function TabScenes({ ctx, onOpenScene }: { ctx: LocationContext | null; onOpenScene: (id: string) => void }) {
  if (!ctx) return <p className="muted" style={{ fontSize: 13 }}>Loading…</p>;
  if (ctx.scenes.length === 0) return (
    <div className="loc-empty"><Icon name="file" size={28} /><p className="muted">No scenes are set here yet. Set a scene's location in the manuscript editor.</p></div>
  );
  return (
    <div className="loc-scenes">
      {ctx.scenes.map((s) => (
        <button key={s.id} className="loc-scene" onClick={() => onOpenScene(s.id)}>
          <Icon name="file" size={14} className="faint" />
          <div className="grow" style={{ minWidth: 0 }}>
            <div className="truncate strong" style={{ fontSize: 13 }}>{s.title}</div>
            <div className="truncate faint" style={{ fontSize: 11 }}>{s.chapterTitle}</div>
          </div>
          <Icon name="chevronRight" size={14} className="faint" />
        </button>
      ))}
    </div>
  );
}

function TabCharacters({ ctx }: { ctx: LocationContext | null }) {
  if (!ctx) return <p className="muted" style={{ fontSize: 13 }}>Loading…</p>;
  if (ctx.characters.length === 0) return (
    <div className="loc-empty"><Icon name="users" size={28} /><p className="muted">No characters linked yet. Add names under “Associated characters”, or set this as a scene's location.</p></div>
  );
  return (
    <div className="loc-chars">
      {ctx.characters.map((c) => (
        <div key={c.id} className="loc-char">
          <CharacterAvatar name={c.name} imageUrl={c.imageData} size="md" />
          <span className="truncate">{c.name}</span>
        </div>
      ))}
    </div>
  );
}

/** Edits a newline-separated list of events as a bullet list. */
function BulletEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const lines = value.split("\n").map((l) => l.trim()).filter(Boolean);
  const [draft, setDraft] = useState("");
  const update = (next: string[]) => onChange(next.join("\n"));
  const add = () => { const v = draft.trim(); if (!v) return; update([...lines, v]); setDraft(""); };
  return (
    <div className="bullet-editor">
      {lines.length > 0 && (
        <ul className="bullet-list">
          {lines.map((l, i) => (
            <li key={i}>
              <span className="grow">{l}</span>
              <button onClick={() => update(lines.filter((_, j) => j !== i))} title="Remove"><Icon name="x" size={12} /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="bullet-add">
        <input className="input" value={draft} placeholder="Add an event…"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <button className="btn btn-sm btn-ghost" onClick={add}><Icon name="plus" size={13} /> Add</button>
      </div>
    </div>
  );
}
